/**
 * AuthService: Core authentication logic
 * Handles login, OAuth exchange, token refresh, and logout
 *
 * FEATURES:
 * - Single-flight refresh: only one refresh call in flight at a time
 * - Refresh token rotation: replaces stored refresh token on each refresh
 * - Supports multiple token response formats from backend
 */

import { api } from '../services/api';
import {
  AuthSession,
  TokenResponseModel,
  NeedsLoginError,
  RefreshFailedError,
} from './authTypes';
import {
  setSession,
  clearSession,
  loadSession,
  getRefreshToken,
  parseExpirationTime,
  setMemoryAccessToken,
  getMemoryAccessToken,
  setRefreshToken,
  setExpiresAt,
  setRememberMe,
} from './authStorage';

/**
 * Single-flight refresh control:
 * If a refresh is in progress, other calls wait for that promise
 */
let refreshPromise: Promise<AuthSession> | null = null;

/**
 * Login with email and password
 * POST /api/v1/account/token
 *
 * @param email User email
 * @param password User password
 * @returns Authenticated session
 */
export const loginWithPassword = async (
  email: string,
  password: string,
  rememberMe: boolean = true
): Promise<AuthSession> => {
  try {
    setRememberMe(rememberMe);
    const response = await api.post<TokenResponseModel>('/v1/account/token', {
      email,
      password,
      provider: 'credentials',
    });

    const session = buildSessionFromTokenResponse(response.data);
    setSession(session);
    return session;
  } catch (error) {
    console.error('Login with password failed:', error);
    throw error;
  }
};

/**
 * Exchange Google or Apple ID token for access token
 * POST /api/v1/account/check-user-status
 *
 * @param idToken ID token from Google/Apple
 * @param provider 'google' | 'apple'
 * @param nonceSha256 Optional nonce SHA256 for additional verification
 * @returns Authenticated session
 */
export const exchangeGoogleApple = async (
  idToken: string,
  provider: 'google' | 'apple',
  nonceSha256?: string
): Promise<AuthSession> => {
  try {
    const response = await api.post<TokenResponseModel>(
      '/v1/account/check-user-status',
      {
        idToken,
        provider,
        nonceSha256,
      }
    );

    const session = buildSessionFromTokenResponse(response.data);
    setSession(session);
    return session;
  } catch (error) {
    console.error(`Exchange ${provider} token failed:`, error);
    throw error;
  }
};

/**
 * Refresh access token using the stored refresh token
 * POST /api/v1/account/refresh
 *
 * This is called by the single-flight wrapper; do not call directly,
 * use refreshTokenSingleFlight() instead
 *
 * @returns New authenticated session
 * @throws NeedsLoginError if refresh token missing
 * @throws RefreshFailedError if refresh fails
 */
const refresh = async (): Promise<AuthSession> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new NeedsLoginError(
      'No refresh token found. User must log in again.'
    );
  }

  try {
    // Get current access token (if any) for Authorization header
    const currentAccessToken = getMemoryAccessToken();

    const response = await api.post<TokenResponseModel>(
      '/v1/account/refresh',
      {
        refreshToken,
        deviceId: getDeviceId(), // Optional device ID
      },
      {
        headers: currentAccessToken
          ? { Authorization: `Bearer ${currentAccessToken}` }
          : {},
      }
    );

    const session = buildSessionFromTokenResponse(response.data);

    // CRITICAL: Update access token and refresh token with new values
    setMemoryAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken);
    setExpiresAt(session.expiresAt);

    return session;
  } catch (error: any) {
    console.error('Token refresh failed:', error);

    // If refresh fails with 401/403, user needs to log in again
    if (error.response?.status === 401 || error.response?.status === 403) {
      clearSession();
      throw new NeedsLoginError('Session expired. Please log in again.');
    }

    throw new RefreshFailedError('Failed to refresh access token.');
  }
};

/**
 * Refresh token with single-flight protection
 * Ensures only one refresh request is in flight at a time
 *
 * If a refresh is already in progress, this waits for that promise
 * and returns its result immediately without making another request
 *
 * @returns New authenticated session
 */
export const refreshTokenSingleFlight = async (): Promise<AuthSession> => {
  // If refresh already in flight, wait for it
  if (refreshPromise !== null) {
    return refreshPromise;
  }

  // Start new refresh, store promise
  refreshPromise = refresh()
    .then((session) => {
      // Clear promise on success
      refreshPromise = null;
      return session;
    })
    .catch((error) => {
      // Clear promise on error (so next call can retry)
      refreshPromise = null;
      throw error;
    });

  return refreshPromise;
};

/**
 * Logout: revoke refresh token on server and clear client state
 * POST /api/v1/account/logout
 *
 * The backend will invalidate all refresh tokens for this user
 * (or just the provided one, depending on implementation)
 */
export const logout = async (): Promise<void> => {
  try {
    const refreshToken = getRefreshToken();
    const accessToken = getMemoryAccessToken();

    // Best-effort revocation (silently fail if network issue)
    if (refreshToken || accessToken) {
      try {
        await api.post(
          '/v1/account/logout',
          { refreshToken },
          {
            headers: accessToken
              ? { Authorization: `Bearer ${accessToken}` }
              : {},
          }
        );
      } catch (error) {
        // Log but don't throw; we'll clear client state regardless
        console.warn(
          'Logout request failed (client state will still be cleared):',
          error
        );
      }
    }
  } finally {
    // Always clear client state, even if server request fails
    clearSession();
    refreshPromise = null;
  }
};

/**
 * Silently refresh access token if it's within 60 seconds of expiry
 * Does nothing if token is still valid
 *
 * Call this periodically or on app startup to keep session alive
 */
export const refreshIfNeeded = async (): Promise<boolean> => {
  const session = loadSession();
  if (!session) {
    return false; // Not logged in
  }

  const now = new Date();
  const expiryThreshold = new Date(now.getTime() + 60 * 1000); // 60 seconds

  if (expiryThreshold > session.expiresAt) {
    try {
      await refreshTokenSingleFlight();
      return true; // Refreshed
    } catch (error) {
      console.error('Proactive refresh failed:', error);
      return false;
    }
  }

  return false; // No refresh needed
};

/**
 * Get current session from memory + localStorage
 * Returns null if not fully loaded
 */
export const getCurrentSession = (): AuthSession | null => {
  return loadSession();
};

/**
 * Build an AuthSession from backend token response
 * Handles multiple formats: 'token' or 'accessToken', various expiry fields
 */
const buildSessionFromTokenResponse = (
  response: TokenResponseModel
): AuthSession => {
  const accessToken = response.accessToken || response.token;
  if (!accessToken) {
    throw new Error('No access token in response from backend');
  }

  const expiresAt = parseExpirationTime(
    response.expires,
    response.expiresAtUtc,
    response.expiresAtEpoch
  );

  return {
    userId: response.id,
    accessToken,
    expiresAt,
    refreshToken: response.refreshToken,
    refreshExpiresAt: response.refreshExpiresAtUtc
      ? new Date(response.refreshExpiresAtUtc)
      : response.refreshExpiresAtEpoch
        ? new Date(response.refreshExpiresAtEpoch)
        : undefined,
    emailConfirmed: response.emailConfirmed || false,
    profileConfirmed: response.profileConfirmed || false,
  };
};

/**
 * Get a stable device ID (generated once, persisted in sessionStorage)
 * Used for refresh token rotation per-device (optional)
 */
const getDeviceId = (): string | undefined => {
  try {
    // Use sessionStorage so device ID is unique per browser tab but consistent within session
    const stored = sessionStorage.getItem('circlebe.deviceId');
    if (stored) {
      return stored;
    }

    // Generate random device ID
    const deviceId = `web_${Math.random().toString(36).substring(2, 11)}_${Date.now()}`;
    sessionStorage.setItem('circlebe.deviceId', deviceId);
    return deviceId;
  } catch (e) {
    console.warn('Could not generate device ID:', e);
    return undefined;
  }
};
